import { Chip, SxProps, Theme } from '@mui/material';
import { forwardRef } from 'react';

/**
 * Type of styled chip variants.
 */
export type ChipColor = 'error' | 'neutral' | 'success' | 'warning';

export interface TaggableChipProps {
  /**
   * The text to display on the chip.
   */
  label: string;
  /**
   * The optional variant of the chip.
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined';
  /**
   * The optional type of the chip, which defines the color and icon.
   * @default 'info'
   */
  color?: ChipColor;
  /**
   * An optional custom icon for the beginning of the chip.
   */
  icon?: JSX.Element & React.ReactElement;
  /**
   * An optional custom icon for the delete action. *Note* requires onDelete
   * function to be visible.
   */
  deleteIcon?: JSX.Element & React.ReactElement;
  /**
   * Optional flag to render the chip as a tag.
   * @default false
   */
  isTag?: boolean;
  /**
   * Optional flag to render the chip as selected.
   * @default false
   */
  isSelected?: boolean;
  /**
   * Optional flag to render the chip as saturated.
   * @default false
   */
  isSaturated?: boolean;
  /**
   * Optional flag to disable the chip.
   * @default false
   */
  disabled?: boolean;
  /**
   * Optional size of the chip.
   */
  size?: 'small' | 'medium';
  /**
   * Optional callback function for onClick events.
   */
  onClick?: () => void;
  /**
   * Optional callback function for onDelete events.
   */
  onDelete?: () => void;
  /**
   * Optional sx prop for custom styling.
   */
  sx?: SxProps<Theme>;
}

export const TaggableChip = forwardRef<HTMLDivElement, TaggableChipProps>(
  (
    {
      label,
      variant,
      color,
      icon,
      deleteIcon,
      isTag,
      isSelected,
      isSaturated,
      disabled,
      size,
      onClick,
      onDelete,
      sx,
    },
    ref
  ) => {
    const classes = [];
    if (isSelected) {
      classes.push('selected');
    }
    if (isTag) {
      classes.push('tag');
    }
    if (isSaturated) {
      classes.push('saturated');
    }

    return (
      <Chip
        ref={ref}
        className={classes.join(' ')}
        label={label}
        variant={variant}
        color={color}
        icon={icon}
        deleteIcon={deleteIcon}
        disabled={disabled}
        size={size}
        onClick={onClick}
        onDelete={deleteIcon && onDelete}
        sx={sx}
      />
    );
  }
);

TaggableChip.displayName = 'TaggableChip';
